import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import { amountToInteger } from '@actual-app/core/shared/util';

import { Boton } from '#components/mobile/ui/Boton';
import { Icono } from '#components/mobile/ui/Icono';
import { ImporteMovimiento } from '#components/mobile/ui/ImporteMovimiento';
import { Tarjeta } from '#components/mobile/ui/Tarjeta';
import { color, espacio, texto } from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useLocale } from '#hooks/useLocale';

import { BocadilloGavi } from './BocadilloGavi';
import { rangoFechas } from './importar';
import type { MovimientoLeido } from './leerExtracto';

type PasoComprobarProps = {
  archivo: string;
  movimientos: MovimientoLeido[];
  /** Solo CSV y Excel: se pueden elegir otras columnas. */
  onCambiarColumnas?: () => void;
  onSeguir: () => void;
};

/** Paso 2: cómo se han leído las tres primeras líneas. */
export function PasoComprobar({
  archivo,
  movimientos,
  onCambiarColumnas,
  onSeguir,
}: PasoComprobarProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const rango = rangoFechas(movimientos);
  const fecha = (dia: string) => monthUtils.format(dia, 'd MMM', locale);

  return (
    <View style={{ flex: 1, gap: 16, padding: espacio.margen }}>
      <Tarjeta
        style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}
        data-testid="importar-archivo"
      >
        <View
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: color.surface2,
            color: color.accent,
          }}
        >
          <Icono nombre="receipt" />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              ...texto.fila,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {archivo}
          </Text>
          <Text style={{ ...texto.secundario, color: color.fg2 }}>
            {rango
              ? t('{{count}} transactions · from {{from}} to {{to}}', {
                  count: movimientos.length,
                  from: fecha(rango.desde),
                  to: fecha(rango.hasta),
                })
              : null}
          </Text>
        </View>
      </Tarjeta>

      <BocadilloGavi>
        <Trans>
          This is how I read the first lines. If it matches your bank, let's go
          on.
        </Trans>
      </BocadilloGavi>

      <Tarjeta
        relleno={0}
        style={{ overflow: 'hidden' }}
        data-testid="importar-muestra-tabla"
      >
        <View
          style={{
            flexDirection: 'row',
            gap: 8,
            padding: '10px 14px',
            backgroundColor: color.surface2,
          }}
        >
          <Text style={{ ...texto.etiqueta, color: color.accent, width: 56 }}>
            <Trans>Date</Trans>
          </Text>
          <Text style={{ ...texto.etiqueta, color: color.accent, flex: 1 }}>
            <Trans>Description</Trans>
          </Text>
          <Text style={{ ...texto.etiqueta, color: color.accent }}>
            <Trans>Amount</Trans>
          </Text>
        </View>
        {movimientos.slice(0, 3).map((m, i) => (
          <View
            key={i}
            data-testid="importar-muestra"
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              padding: '12px 14px',
              borderTop: `1px solid ${color.line}`,
            }}
          >
            <Text style={{ ...texto.cuerpo, color: color.fg2, width: 56 }}>
              {fecha(m.fecha)}
            </Text>
            <Text
              style={{
                ...texto.cuerpo,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {m.concepto || '—'}
            </Text>
            <PrivacyFilter>
              <ImporteMovimiento valor={amountToInteger(m.importe)} />
            </PrivacyFilter>
          </View>
        ))}
      </Tarjeta>

      {onCambiarColumnas && (
        <Text
          style={{ ...texto.secundario, color: color.fg2, textAlign: 'center' }}
          data-testid="importar-recordare"
        >
          <Trans>I'll remember this format next time.</Trans>
        </Text>
      )}

      <View style={{ flex: 1 }} />

      <Boton bloque onPress={onSeguir} data-testid="importar-bien">
        <Trans>Yes, that's right</Trans>
      </Boton>
      {onCambiarColumnas && (
        <Boton
          bloque
          variante="fantasma"
          onPress={onCambiarColumnas}
          data-testid="importar-cambiar-columnas"
        >
          <Trans>No, change columns</Trans>
        </Boton>
      )}
    </View>
  );
}
